import React, { useState } from 'react';
import type { RecoveryContract, RecoveryRequirement } from '../src/types';

const CLASSIFICATIONS = ['REQUIRED', 'LIKELY', 'OPTIONAL', 'FORBIDDEN'] as const;

function StateBlock({
  title,
  state,
  onChange,
}: {
  title: string;
  state: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const entries = Object.entries(state);
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-cv-primary">{title}</h4>
      {entries.length === 0 ? (
        <p className="text-xs text-cv-muted">None specified.</p>
      ) : (
        <div className="space-y-2">
          {entries.map(([key, value], i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="text"
                value={key}
                onChange={(e) => {
                  const next = { ...state };
                  delete next[key];
                  next[e.target.value] = value;
                  onChange(next);
                }}
                className="bg-cv-page border border-cv-border-strong rounded px-2 py-1 text-xs text-cv-secondary"
                placeholder="Key"
              />
              <input
                type="text"
                value={value}
                onChange={(e) => onChange({ ...state, [key]: e.target.value })}
                className="sm:col-span-2 bg-cv-page border border-cv-border-strong rounded px-2 py-1 text-xs text-cv-secondary"
                placeholder="Value"
              />
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => onChange({ ...state, '': '' })}
        className="text-xs text-cv-gold hover:text-cv-interactive"
      >
        + Add field
      </button>
    </section>
  );
}

function RequirementEditor({
  requirement,
  index,
  onChange,
  onRemove,
  structuralWarning,
}: {
  requirement: RecoveryRequirement;
  index: number;
  onChange: (index: number, next: RecoveryRequirement) => void;
  onRemove: (index: number) => void;
  structuralWarning?: boolean;
}) {
  return (
    <div className="rounded border border-cv-border-strong bg-cv-elevated/40 p-3 space-y-2">
      <div className="flex flex-wrap gap-2 items-start justify-between">
        <select
          value={requirement.classification}
          onChange={(e) =>
            onChange(index, {
              ...requirement,
              classification: e.target.value as RecoveryRequirement['classification'],
            })
          }
          className="bg-cv-page border border-cv-border-strong rounded px-2 py-1 text-xs text-cv-primary"
        >
          {CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {requirement.classification === 'OPTIONAL' && (
          <button type="button" onClick={() => onRemove(index)} className="text-xs text-cv-danger">
            Remove
          </button>
        )}
      </div>
      <textarea
        value={requirement.description}
        onChange={(e) => onChange(index, { ...requirement, description: e.target.value })}
        rows={2}
        className="w-full bg-cv-page border border-cv-border-strong rounded px-2 py-1 text-sm text-cv-primary resize-y"
      />
      {requirement.evidenceSources?.length > 0 && (
        <div className="text-xs text-cv-muted space-y-1">
          <p className="font-medium text-cv-muted">Evidence (read-only)</p>
          {requirement.evidenceSources.map((src, si) => (
            <p key={si}>
              [{src.sourceType}
              {src.chapterNumber != null ? ` Ch.${src.chapterNumber}` : ''}] {src.evidenceNote}
            </p>
          ))}
        </div>
      )}
      {structuralWarning && requirement.classification !== 'OPTIONAL' && (
        <p className="text-xs text-cv-warning">
          Removing or weakening structural requirements may break continuity with later chapters.
        </p>
      )}
    </div>
  );
}

export interface RecoveryContractViewProps {
  contract: RecoveryContract;
  onChange: (contract: RecoveryContract) => void;
  readOnly?: boolean;
}

export function RecoveryContractView({ contract, onChange, readOnly = false }: RecoveryContractViewProps) {
  const [removeStructuralWarn, setRemoveStructuralWarn] = useState(false);

  const updateRequirement = (index: number, next: RecoveryRequirement) => {
    const requirements = [...contract.requirements];
    requirements[index] = next;
    onChange({ ...contract, requirements });
  };

  const removeRequirement = (index: number) => {
    const req = contract.requirements[index];
    if (req.classification !== 'OPTIONAL') {
      setRemoveStructuralWarn(true);
      return;
    }
    onChange({
      ...contract,
      requirements: contract.requirements.filter((_, i) => i !== index),
    });
  };

  const addRequirement = () => {
    onChange({
      ...contract,
      requirements: [
        ...contract.requirements,
        {
          description: '',
          classification: 'OPTIONAL',
          evidenceSources: [],
          confidence: 0.5,
        },
      ],
    });
  };

  const required = contract.requirements.filter((r) => r.classification === 'REQUIRED');
  const likely = contract.requirements.filter((r) => r.classification === 'LIKELY');
  const optional = contract.requirements.filter((r) => r.classification === 'OPTIONAL');
  const forbidden = contract.requirements.filter((r) => r.classification === 'FORBIDDEN');

  const renderGroup = (title: string, items: RecoveryRequirement[], tone: string) => {
    if (!items.length) return null;
    return (
      <section className="space-y-2">
        <h4 className={`text-sm font-semibold ${tone}`}>{title}</h4>
        {contract.requirements.map((req, i) =>
          items.includes(req) ? (
            <RequirementEditor
              key={i}
              requirement={req}
              index={i}
              onChange={readOnly ? () => {} : updateRequirement}
              onRemove={readOnly ? () => {} : removeRequirement}
              structuralWarning={removeStructuralWarn}
            />
          ) : null,
        )}
      </section>
    );
  };

  return (
    <div className="space-y-5 text-sm">
      <p className="text-xs text-cv-muted">
        Recovery plan for Chapter {contract.missingChapterNumber} — reconstructed replacement (not the
        original).
      </p>

      {readOnly ? (
        <>
          <StateBlock title="Required opening state" state={contract.requiredOpeningState} onChange={() => {}} />
        </>
      ) : (
        <StateBlock
          title="Required opening state"
          state={contract.requiredOpeningState}
          onChange={(requiredOpeningState) => onChange({ ...contract, requiredOpeningState })}
        />
      )}

      {renderGroup('Events that must occur', required, 'text-green-300')}
      {renderGroup('Likely connective events', likely, 'text-cv-interactive')}
      {renderGroup('Optional details', optional, 'text-cv-secondary')}
      {renderGroup('Contradictions to avoid', forbidden, 'text-cv-danger')}

      {!readOnly && (
        <button type="button" onClick={addRequirement} className="text-xs text-cv-gold">
          + Add requirement
        </button>
      )}

      {readOnly ? (
        <StateBlock title="Required closing state" state={contract.requiredClosingState} onChange={() => {}} />
      ) : (
        <StateBlock
          title="Required closing state"
          state={contract.requiredClosingState}
          onChange={(requiredClosingState) => onChange({ ...contract, requiredClosingState })}
        />
      )}

      <section className="space-y-2">
        <h4 className="text-sm font-semibold text-cv-warning">Unresolved uncertainties</h4>
        {(contract.unresolvedUncertainties ?? []).map((u, i) => (
          <input
            key={i}
            type="text"
            value={u}
            readOnly={readOnly}
            onChange={(e) => {
              const unresolvedUncertainties = [...contract.unresolvedUncertainties];
              unresolvedUncertainties[i] = e.target.value;
              onChange({ ...contract, unresolvedUncertainties });
            }}
            className="w-full bg-cv-page border border-cv-border-strong rounded px-2 py-1 text-xs text-cv-secondary"
          />
        ))}
        {!readOnly && (
          <button
            type="button"
            onClick={() =>
              onChange({
                ...contract,
                unresolvedUncertainties: [...(contract.unresolvedUncertainties ?? []), ''],
              })
            }
            className="text-xs text-cv-gold"
          >
            + Add uncertainty note
          </button>
        )}
      </section>

      <section className="grid grid-cols-2 gap-3">
        {(['pov', 'tense', 'tone'] as const).map((field) => (
          <div key={field}>
            <label className="text-xs text-cv-muted uppercase">{field}</label>
            <input
              type="text"
              readOnly={readOnly}
              value={contract.styleRequirements?.[field] ?? ''}
              onChange={(e) =>
                onChange({
                  ...contract,
                  styleRequirements: { ...contract.styleRequirements, [field]: e.target.value },
                })
              }
              className="w-full mt-0.5 bg-cv-page border border-cv-border-strong rounded px-2 py-1 text-xs text-cv-secondary"
            />
          </div>
        ))}
        <div>
          <label className="text-xs text-cv-muted uppercase">target words</label>
          <input
            type="number"
            readOnly={readOnly}
            value={contract.styleRequirements?.targetWordCount ?? ''}
            onChange={(e) =>
              onChange({
                ...contract,
                styleRequirements: {
                  ...contract.styleRequirements,
                  targetWordCount: parseInt(e.target.value, 10) || undefined,
                },
              })
            }
            className="w-full mt-0.5 bg-cv-page border border-cv-border-strong rounded px-2 py-1 text-xs text-cv-secondary"
          />
        </div>
      </section>
    </div>
  );
}
